import { Center, forwardRef, Input, PopoverTrigger, Spacer, useFormControl, useToken, VisuallyHidden } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ButtonProps } from '@bonusly/ds-button';
import { XCircleOutline } from '@bonusly/ds-icon';
import { Tooltip } from '@bonusly/ds-tooltip';
import { rem } from '@bonusly/ds-utils';

import { assertIsDateValue } from '../../types';
import { useDatePickerContext } from '../context';

type DatePickerInputProps = ButtonProps & {
	children?: ReactNode | ((props: { value: Date | null }) => ReactNode);
};

export const DatePickerInput = forwardRef<DatePickerInputProps, 'button'>(({ children, ...rest }, ref) => {
	const errorColor = useToken('colors', 'error.inputBorder');
	const formProps = useFormControl({ readOnly: true });
	const { t } = useTranslation('components', { keyPrefix: 'date_picker' });
	const { displayFormat, isClearable, onClear, onOpen, value } = useDatePickerContext();

	assertIsDateValue(value);

	const renderedChildren = (() => {
		if (typeof children === 'function') {
			return children({ value });
		}

		if (children) {
			return children;
		}

		if (value) {
			return t('date_value', {
				context: displayFormat,
				date: value,
				interpolation: { escapeValue: false },
			});
		}

		return t('date_placeholder');
	})();

	return (
		<>
			<VisuallyHidden>
				<Input value={value ? value.toISOString() : ''} {...formProps} />
			</VisuallyHidden>
			<PopoverTrigger>
				<Button
					alignItems="center"
					bg="bg.surface"
					borderColor="border.default"
					borderStyle="solid"
					borderWidth="1px"
					color={value ? 'text.default' : 'text.softest'}
					fontWeight="normal"
					minH={rem(40)}
					minW={rem(192)}
					onClick={onOpen}
					px={3}
					py={2}
					ref={ref}
					_active={{
						borderColor: 'border.focus',
						outline: '1px solid',
						outlineOffset: '0',
						_disabled: {
							borderColor: 'border.default',
							outline: 'none',
							outlineOffset: '0',
						},
					}}
					_hover={{
						bg: 'bg.soft',
					}}
					_invalid={{
						borderColor: 'error.inputBorder',
						boxShadow: `0 0 0 1px ${errorColor}`,
					}}
					{...formProps}
					{...rest}
					{...(value &&
						isClearable && {
							rightIcon: (
								<Tooltip label={t('clear')} placement="bottom">
									<Center
										tabIndex={0}
										role="button"
										onClick={(e) => {
											e.stopPropagation();
											onClear();
										}}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												e.stopPropagation();
												onClear();
											}
										}}
									>
										<XCircleOutline
											color="text.softest"
											aria-label={t('clear')}
											ml={2}
											size="md"
											_hover={{
												color: 'text.default',
											}}
										/>
									</Center>
								</Tooltip>
							),
						})}
				>
					{renderedChildren}
					<Spacer />
				</Button>
			</PopoverTrigger>
		</>
	);
});

DatePickerInput.displayName = 'DatePickerInput';
